<template>
  <aside
    class="h-screen flex flex-col relative z-50 select-none border-r transition-all duration-300 ease-in-out shadow-2xl sidebar-branded"
    :class="[isCollapsed ? 'w-20' : 'w-72']"
    :style="{
      ...brandingCSSVars,
      backgroundColor: 'var(--sidebar-bg)',
      color: 'var(--sidebar-text)',
      borderColor: 'var(--sidebar-border)'
    }"
  >
    <!-- TOGGLE BUTTON -->
    <button
      @click="isCollapsed = !isCollapsed"
      class="absolute -right-3.5 top-7 w-7 h-7 rounded-full border text-white flex items-center justify-center shadow-lg transition-transform duration-200 hover:scale-110 z-50 cursor-pointer"
      :style="{
        backgroundColor: branding?.sidebarColors?.activeButton || '#3b82f6',
        borderColor: branding?.sidebarColors?.border || '#475569'
      }"
      :title="isCollapsed ? 'Déplier' : 'Replier'"
    >
      <svg
        class="w-4 h-4 transition-transform duration-300"
        :class="{ 'rotate-180': isCollapsed }"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 19l-7-7 7-7" />
      </svg>
    </button>

    <!-- HEADER / LOGO -->
    <div
      class="h-20 flex items-center px-6 border-b shrink-0 bg-opacity-40"
      :style="{
        borderColor: branding?.sidebarColors?.border || 'rgba(148, 163, 184, 0.5)',
        backgroundColor: branding?.sidebarColors?.background
          ? `${branding.sidebarColors.background}cc`
          : 'rgba(15, 23, 42, 0.4)'
      }"
    >
      <div class="flex items-center gap-3 w-full overflow-hidden">
        <!-- Logo personnalisé si disponible -->
        <img
          v-if="branding?.logo?.url"
          :src="branding.logo.url"
          class="h-auto w-full p-10 object-contain shrink-0 transition-all duration-300"
          :class="[isCollapsed ? 'scale-90 mx-auto' : '']"
          alt="Logo agence"
        />
        <!-- Logo par défaut -->
        <img
          v-else
          src="../assets/gemini-svg.svg"
          class="h-auto w-full p-10 object-contain shrink-0 transition-all duration-300"
          :class="[isCollapsed ? 'scale-90 mx-auto' : '']"
          alt="Medialink Logo"
        />
       
      </div>
    </div>

    <!-- LIVE STATS SECTION (Masqué pour l'admin) -->
    <div
      v-if="!isCollapsed && showLiveStats && userData.role !== 'AGENCY_ADMIN'"
      class="px-3 py-4 border-b shrink-0 space-y-4"
      :style="{
        borderColor: branding?.sidebarColors?.border || 'rgba(148, 163, 184, 0.5)'
      }"
    >
      <div class="flex items-center justify-between px-1">
        <h3 class="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">
          {{ t.liveStats }}
        </h3>
        <div class="flex items-center gap-1">
          <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
          <span class="text-[9px] text-emerald-500 font-bold">LIVE</span>
        </div>
      </div>

      <!-- FILES D'ATTENTE -->
      <div class="space-y-2">
        <p class="text-[9px] font-bold text-slate-400 uppercase tracking-wider px-1">
          {{ t.queues }}
        </p>
        <div v-if="loadingStats" class="text-center py-4">
          <div class="inline-block w-4 h-4 border-2 border-slate-700 border-t-blue-500 rounded-full animate-spin"></div>
        </div>
        <div v-else-if="queuesStats.length === 0" class="text-xs text-slate-500 text-center py-2">
          {{ t.noQueues }}
        </div>
        <div v-else class="space-y-1.5">
          <div
            v-for="queue in queuesStats"
            :key="queue._id"
            class="queue-stat-card group relative p-2 rounded-lg bg-slate-900/60 border transition-all duration-200 hover:bg-slate-900/80"
            :class="getQueueUrgencyClass(queue.waitingCount)"
          >
            <div class="flex items-center justify-between mb-1">
              <span class="text-xs font-bold text-slate-200 truncate">
                {{ queue.name }}
              </span>
              <span
                class="px-1.5 py-0.5 rounded text-[10px] font-black tabular-nums"
                :class="getQueueCountClass(queue.waitingCount)"
              >
                {{ queue.waitingCount }}
              </span>
            </div>
            <div class="flex items-center gap-2 text-[9px] text-slate-400">
              <span class="flex items-center gap-1">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                ~{{ queue.avgWaitTime }} min
              </span>
              <span v-if="queue.maxWaitTime > 15" class="text-orange-400 font-bold animate-pulse">
                Max: {{ queue.maxWaitTime }} min
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- APPELS EN COURS -->
      <div class="space-y-2">
        <p class="text-[9px] font-bold text-slate-400 uppercase tracking-wider px-1">
          {{ t.currentCalls }}
        </p>
        <div v-if="activeCounterCalls.length === 0" class="text-xs text-slate-500 text-center py-2">
          {{ t.noCalls }}
        </div>
        <div v-else class="space-y-1.5 max-h-40 overflow-y-auto scrollbar-thin">
          <div
            v-for="call in activeCounterCalls.slice(0, 5)"
            :key="call.ticketId"
            class="p-2 rounded-lg bg-blue-900/20 border border-blue-800/30 hover:border-blue-700/50 transition-all duration-200"
          >
            <div class="flex items-center justify-between mb-0.5">
              <span class="text-xs font-bold text-blue-300">
                {{ call.counterName }}
              </span>
              <span class="text-[9px] text-slate-400">
                {{ call.timeAgo }}
              </span>
            </div>
            <div class="text-xs font-black text-white tabular-nums">
              {{ call.ticketNumber }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- NAVIGATION -->
    <nav class="flex-1 px-3 py-6 space-y-6 overflow-y-auto scrollbar-none">
      
      <!-- ROLE: EMPLOYEE -->
      <div v-if="userData.role === 'EMPLOYEE'" class="space-y-1">
        <p v-if="!isCollapsed" class="px-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 mb-2">
          {{ t.myPost }}
        </p>
        <RouterLink
          to="/user-client"
          class="nav-link group"
          active-class="nav-link-active"
          :title="isCollapsed ? t.customerCall : ''"
        >
          <!-- Phone Call Icon -->
          <svg class="w-5 h-5 shrink-0 transition-transform duration-200 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M15.042 21.672L13.684 16.6m0 0l-2.51 2.225.569-9.47 5.227 7.917-3.286-.672zm-7.518-2.672a10 10 0 01-5.18-8.71C2.344 4.8 6.544 1 11.7 1c4.982 0 9.08 3.568 9.61 8.28" />
          </svg>
          <span v-if="!isCollapsed" class="truncate text-sm font-medium">{{ t.customerCall }}</span>
        </RouterLink>
      </div>

      <!-- ROLE: BACK_OFFICE -->
      <div v-if="userData.role === 'BACK_OFFICE'" class="space-y-1">
        <p v-if="!isCollapsed" class="px-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 mb-2">
          {{ t.supervision }}
        </p>
        <RouterLink
          to="/back-office"
          class="nav-link group"
          active-class="nav-link-active"
          :title="isCollapsed ? t.backOfficeOverview : ''"
        >
          <!-- Monitor / Overview Icon -->
          <svg class="w-5 h-5 shrink-0 transition-transform duration-200 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v10a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM8 21h8m-4-4v4" />
          </svg>
          <span v-if="!isCollapsed" class="truncate text-sm font-medium">{{ t.backOfficeOverview }}</span>
        </RouterLink>
      </div>

      <!-- ROLE: AGENCY_ADMIN -->
      <div v-if="userData.role === 'AGENCY_ADMIN'" class="space-y-6">
        
        <!-- SECTION 1: PILOTAGE -->
        <div class="space-y-1">
          <p v-if="!isCollapsed" class="px-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 mb-2">
            {{ t.pilotage }}
          </p>
          
          <!-- Dashboard -->
          <RouterLink to="/" class="nav-link group" active-class="nav-link-active" :title="isCollapsed ? t.dashboard : ''">
            <svg class="w-5 h-5 shrink-0 transition-transform duration-200 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M4 5a2 2 0 012-2h4a2 2 0 012 2v4a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm0 10a2 2 0 012-2h4a2 2 0 012 2v4a2 2 0 01-2 2H6a2 2 0 01-2-2v-4zm10-10a2 2 0 012-2h4a2 2 0 012 2v4a2 2 0 01-2 2h-4a2 2 0 01-2-2V5zm0 10a2 2 0 012-2h4a2 2 0 012 2v4a2 2 0 01-2 2h-4a2 2 0 01-2-2v-4z" />
            </svg>
            <span v-if="!isCollapsed" class="truncate text-sm font-medium">{{ t.dashboard }}</span>
          </RouterLink>

          <!-- Console Admin -->
          <RouterLink to="/admin-client" class="nav-link group" active-class="nav-link-active" :title="isCollapsed ? t.adminConsole : ''">
            <svg class="w-5 h-5 shrink-0 transition-transform duration-200 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span v-if="!isCollapsed" class="truncate text-sm font-medium">{{ t.adminConsole }}</span>
          </RouterLink>

          <!-- Tickets -->
          <RouterLink to="/tickets" class="nav-link group" active-class="nav-link-active" :title="isCollapsed ? t.tickets : ''">
            <svg class="w-5 h-5 shrink-0 transition-transform duration-200 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
            </svg>
            <span v-if="!isCollapsed" class="truncate text-sm font-medium">{{ t.tickets }}</span>
          </RouterLink>

          <!-- Rapport Ticketing -->
          <RouterLink to="/rapport-ticketing" class="nav-link group" active-class="nav-link-active" :title="isCollapsed ? t.reportTicketing : ''">
            <svg class="w-5 h-5 shrink-0 transition-transform duration-200 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <span v-if="!isCollapsed" class="truncate text-sm font-medium">{{ t.reportTicketing }}</span>
          </RouterLink>
        </div>

        <!-- SECTION 2: BORNES & ECRANS -->
        <div class="space-y-1">
          <p v-if="!isCollapsed" class="px-3 text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 mb-2">
            {{ t.devicesScreens }}
          </p>
          <!-- Bornes / Kiosk Device Icon -->
          <RouterLink to="/kiosk" class="nav-link group" active-class="nav-link-active" :title="isCollapsed ? t.devicesList : ''">
            <svg class="w-5 h-5 shrink-0 transition-transform duration-200 group-hover:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <span v-if="!isCollapsed" class="truncate text-sm font-medium">{{ t.devicesList }}</span>
          </RouterLink>
        </div>

      </div>
    </nav>

    <!-- FOOTER / PROFIL & PARAMETRES -->
    <div
      class="p-3 border-t space-y-3 shrink-0 bg-opacity-40"
      :style="{
        borderColor: branding?.sidebarColors?.border || 'rgba(148, 163, 184, 0.5)',
        backgroundColor: branding?.sidebarColors?.background
          ? `${branding.sidebarColors.background}cc`
          : 'rgba(15, 23, 42, 0.4)'
      }"
    >
      
      <!-- CARTE UTILISATEUR -->
      <div 
        class="flex items-center gap-3 p-2.5 rounded-xl transition-all duration-200 bg-slate-900/80 border border-slate-800 shadow-inner"
        :class="[isCollapsed ? 'justify-center' : '']"
      >
        <div class="relative shrink-0">
          <div
            class="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-sm shadow-md"
            :style="{
              backgroundColor: branding?.sidebarColors?.activeButton || '#3b82f6'
            }"
          >
            {{ userData.firstName ? userData.firstName[0] : 'A' }}
          </div>
          <span class="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-slate-950"></span>
        </div>
        
        <div v-if="!isCollapsed" class="overflow-hidden">
          <p
            class="text-xs font-semibold truncate leading-tight"
            :style="{ color: branding?.sidebarColors?.text || '#e2e8f0' }"
          >
            {{ userData.firstName }} {{ userData.lastName }}
          </p>
          <p
            class="text-[10px] font-bold tracking-wide uppercase truncate mt-0.5"
            :style="{ color: branding?.sidebarColors?.activeButton || '#60a5fa' }"
          >
            {{ userData.role === 'AGENCY_ADMIN' ? t.administrator : (userData.role === 'BACK_OFFICE' ? t.backOffice : t.agent) }}
          </p>
        </div>
      </div>

      <!-- LANGUE -->
      <div 
        class="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-slate-900/60 border border-slate-800 transition-all hover:border-slate-700"
        :class="[isCollapsed ? 'justify-center' : '']"
      >
        <!-- Globe SVG -->
        <svg class="w-4 h-4 text-slate-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.8" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
        </svg>
        <select 
          v-if="!isCollapsed"
          v-model="currentLanguage" 
          @change="changeLanguage"
          class="bg-transparent border-none text-xs font-medium text-slate-300 focus:ring-0 cursor-pointer w-full p-0 outline-none"
        >
          <option value="fr" class="bg-slate-900 text-slate-200">Français</option>
          <option value="en" class="bg-slate-900 text-slate-200">English</option>
          <option value="de" class="bg-slate-900 text-slate-200">Deutsch</option>
          <option value="it" class="bg-slate-900 text-slate-200">Italiano</option>
        </select>
      </div>

      <!-- DECONNEXION -->
      <button
        @click="logout"
        class="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group text-rose-400 hover:bg-rose-500/10 hover:text-rose-300 border border-transparent hover:border-rose-500/20 cursor-pointer"
        :class="[isCollapsed ? 'justify-center' : '']"
        :title="isCollapsed ? t.logout : ''"
      >
        <svg class="w-5 h-5 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 11-4 0v-1m0-10V5a2 2 0 114 0v1" />
        </svg>
        <span v-if="!isCollapsed" class="text-xs font-bold whitespace-nowrap">{{ t.logout }}</span>
      </button>

      <!-- FOOTER TEXT -->
      <p v-if="!isCollapsed" class="text-[9px] text-center text-slate-600 font-bold tracking-widest uppercase pt-1">
        {{ t.madeBy }}
      </p>
    </div>
  </aside>
</template>

<script>
import { RouterLink } from "vue-router";
import { io } from "socket.io-client";

export default {
  components: { RouterLink },
  data() {
    return {
      isCollapsed: false,
      currentLanguage: localStorage.getItem("appLanguage") || "fr",
      userData: JSON.parse(localStorage.getItem("userData") || "{}"),
      showLiveStats: true,
      loadingStats: true,
      queuesStats: [],
      activeCounterCalls: [],
      socket: null,
      refreshInterval: null,
      branding: null,
      translations: {
        fr: {
          pilotage: "Pilotage",
          dashboard: "Dashboard",
          adminConsole: "Console Admin",
          tickets: "Tickets",
          reportTicketing: "Rapport Ticketing",
          devicesScreens: "Bornes & Écrans",
          devicesList: "Liste des Périphériques",
          logout: "Déconnexion",
          myPost: "Mon Poste",
          customerCall: "Appel Client",
          administrator: "Administrateur",
          agent: "Agent",
          backOffice: "Back Office",
          supervision: "Supervision",
          backOfficeOverview: "Vue d'Ensemble",
          madeBy: "© Réalisé par Datafuse",
          liveStats: "Stats Temps Réel",
          queues: "Files d'Attente",
          currentCalls: "Appels en Cours",
          noQueues: "Aucune file active",
          noCalls: "Aucun appel en cours"
        },
        en: {
          pilotage: "Management",
          dashboard: "Dashboard",
          adminConsole: "Admin Console",
          tickets: "Tickets",
          reportTicketing: "Ticketing Report",
          devicesScreens: "Kiosks & Screens",
          devicesList: "Devices List",
          logout: "Logout",
          myPost: "My Station",
          customerCall: "Customer Call",
          administrator: "Administrator",
          agent: "Agent",
          backOffice: "Back Office",
          supervision: "Supervision",
          backOfficeOverview: "Overview",
          madeBy: "© Made by Datafuse",
          liveStats: "Live Stats",
          queues: "Queues",
          currentCalls: "Current Calls",
          noQueues: "No active queues",
          noCalls: "No calls in progress"
        },
        de: {
          pilotage: "Steuerung",
          dashboard: "Dashboard",
          adminConsole: "Admin-Konsole",
          tickets: "Tickets",
          reportTicketing: "Ticketing-Bericht",
          devicesScreens: "Kioske & Bildschirme",
          devicesList: "Geräteliste",
          logout: "Abmelden",
          myPost: "Mein Arbeitsplatz",
          customerCall: "Kundenanruf",
          administrator: "Administrator",
          agent: "Mitarbeiter",
          backOffice: "Back Office",
          supervision: "Überwachung",
          backOfficeOverview: "Überblick",
          madeBy: "© Erstellt von Datafuse",
          liveStats: "Live-Statistiken",
          queues: "Warteschlangen",
          currentCalls: "Aktuelle Anrufe",
          noQueues: "Keine aktiven Warteschlangen",
          noCalls: "Keine Anrufe aktiv"
        },
        it: {
          pilotage: "Gestione",
          dashboard: "Dashboard",
          adminConsole: "Console Admin",
          tickets: "Ticket",
          reportTicketing: "Rapporto Ticketing",
          devicesScreens: "Chioschi & Schermi",
          devicesList: "Elenco Dispositivi",
          logout: "Disconnetti",
          myPost: "La Mia Postazione",
          customerCall: "Chiamata Cliente",
          administrator: "Amministratore",
          agent: "Agente",
          backOffice: "Back Office",
          supervision: "Supervisione",
          backOfficeOverview: "Panoramica",
          madeBy: "© Realizzato da Datafuse",
          liveStats: "Statistiche Live",
          queues: "Code",
          currentCalls: "Chiamate Attuali",
          noQueues: "Nessuna coda attiva",
          noCalls: "Nessuna chiamata in corso"
        }
      }
    };
  },
  computed: {
    t() {
      return this.translations[this.currentLanguage] || this.translations.fr;
    },

    brandingCSSVars() {
      if (!this.branding?.sidebarColors) {
        return {
          '--sidebar-bg': '#0B0F19',
          '--sidebar-text': '#E8EBF5',
          '--sidebar-text-muted': '#8A90B3',
          '--sidebar-active': '#3b82f6',
          '--sidebar-hover': '#4f46e5',
          '--sidebar-border': '#1F2A4A'
        };
      }

      const colors = this.branding.sidebarColors;
      return {
        '--sidebar-bg': colors.background || '#0B0F19',
        '--sidebar-text': colors.text || '#E8EBF5',
        '--sidebar-text-muted': colors.textMuted || '#8A90B3',
        '--sidebar-active': colors.activeButton || '#3b82f6',
        '--sidebar-hover': colors.hoverButton || '#4f46e5',
        '--sidebar-border': colors.border || '#1F2A4A'
      };
    }
  },
  methods: {
    changeLanguage() {
      localStorage.setItem("appLanguage", this.currentLanguage);
      window.location.reload();
    },
    logout() {
      localStorage.removeItem("userToken");
      localStorage.removeItem("userData");
      this.$router.push("/login");
    },

    // ========== BRANDING METHODS ==========

    async loadBranding(forceRefresh = false) {
      try {
        const token = localStorage.getItem("userToken");
        if (!token) {
          console.warn("⚠️ [Navbar] Pas de token, branding non chargé");
          return;
        }

        // Vérifier le cache (valide pendant 5 minutes seulement)
        if (!forceRefresh) {
          const cached = localStorage.getItem("agencyBranding");
          const cachedTimestamp = localStorage.getItem("agencyBrandingTimestamp");
          const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

          if (cached && cachedTimestamp) {
            const age = Date.now() - parseInt(cachedTimestamp);
            if (age < CACHE_DURATION) {
              this.branding = JSON.parse(cached);
              console.log("📦 [Navbar] Branding chargé depuis le cache");
            }
          }
        }

        // Toujours charger depuis l'API pour avoir les dernières données
        const response = await fetch("https://api.datafuse.fr/api/agency/branding", {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });

        if (response.ok) {
          const brandingData = await response.json();
          this.branding = brandingData;

          // Mettre en cache
          localStorage.setItem("agencyBranding", JSON.stringify(brandingData));
          localStorage.setItem("agencyBrandingTimestamp", Date.now().toString());

          console.log("✅ [Navbar] Branding chargé depuis l'API:", brandingData);
        } else {
          console.warn("⚠️ [Navbar] Impossible de charger le branding, status:", response.status);
          if (response.status === 403 || response.status === 401) {
            console.error("❌ [Navbar] Token invalide ou expiré");
          }
        }
      } catch (err) {
        console.error("❌ [Navbar] Erreur chargement branding:", err);
      }
    },

    // Méthode exposée globalement pour forcer le rechargement
    refreshBranding() {
      localStorage.removeItem("agencyBranding");
      localStorage.removeItem("agencyBrandingTimestamp");
      this.loadBranding(true);
    },

    // ========== STATS METHODS ==========

    async fetchQueuesStats() {
      try {
        const token = localStorage.getItem("userToken");
        if (!token) return;

        const response = await fetch("https://api.datafuse.fr/api/agency/queues-stats", {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });

        if (response.ok) {
          this.queuesStats = await response.json();
        }
      } catch (err) {
        console.error("❌ [Navbar] Erreur fetch queues stats:", err);
      } finally {
        this.loadingStats = false;
      }
    },

    async fetchActiveCounterCalls() {
      try {
        const token = localStorage.getItem("userToken");
        if (!token) return;

        const response = await fetch("https://api.datafuse.fr/api/agency/active-counter-calls", {
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });

        if (response.ok) {
          this.activeCounterCalls = await response.json();
        }
      } catch (err) {
        console.error("❌ [Navbar] Erreur fetch active calls:", err);
      }
    },

    initWebSocket() {
      const token = localStorage.getItem("userToken");
      if (!token) return;

      try {
        this.socket = io("https://api.datafuse.fr", {
          auth: { token },
          reconnection: true,
          reconnectionDelay: 1000,
          reconnectionAttempts: 5
        });

        this.socket.on("connect", () => {
          console.log("🟢 [Navbar WS] Connecté");
        });

        this.socket.on("disconnect", () => {
          console.log("🔴 [Navbar WS] Déconnecté");
        });

        // Mise à jour lors des événements de tickets
        this.socket.on("queue_updated", () => {
          this.fetchQueuesStats();
        });

        this.socket.on("ticket_called", () => {
          this.fetchQueuesStats();
          this.fetchActiveCounterCalls();
        });

        this.socket.on("new_ticket_waiting", () => {
          this.fetchQueuesStats();
        });

        this.socket.on("ticket_finished", () => {
          this.fetchQueuesStats();
          this.fetchActiveCounterCalls();
        });

        this.socket.on("ticket_auto_finished", () => {
          this.fetchQueuesStats();
          this.fetchActiveCounterCalls();
        });

      } catch (err) {
        console.error("❌ [Navbar WS] Erreur init:", err);
      }
    },

    getQueueUrgencyClass(waitingCount) {
      if (waitingCount === 0) {
        return "border-slate-700/50";
      } else if (waitingCount < 5) {
        return "border-yellow-500/30";
      } else if (waitingCount < 10) {
        return "border-orange-500/40";
      } else {
        return "border-red-500/50 animate-pulse";
      }
    },

    getQueueCountClass(waitingCount) {
      if (waitingCount === 0) {
        return "bg-slate-700/50 text-slate-400";
      } else if (waitingCount < 5) {
        return "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30";
      } else if (waitingCount < 10) {
        return "bg-orange-500/20 text-orange-400 border border-orange-500/30";
      } else {
        return "bg-red-500/20 text-red-400 border border-red-500/30";
      }
    }
  },

  mounted() {
    // Charger le branding de l'agence
    this.loadBranding();

    // Exposer la méthode de rechargement globalement (pour debug)
    window.$refreshBranding = () => this.refreshBranding();

    // Charger les stats initiales
    if (this.userData.agencyId) {
      this.fetchQueuesStats();
      this.fetchActiveCounterCalls();

      // Initialiser WebSocket
      this.initWebSocket();

      // Rafraîchir toutes les 30 secondes (fallback si WS déconnecté)
      this.refreshInterval = setInterval(() => {
        this.fetchQueuesStats();
        this.fetchActiveCounterCalls();
      }, 30000);
    }
  },

  beforeUnmount() {
    // Nettoyer
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    if (this.socket) {
      this.socket.disconnect();
    }
  }
};
</script>

<style scoped>
/* Masquer la barre de défilement */
.scrollbar-none::-webkit-scrollbar { display: none; }
.scrollbar-none { -ms-overflow-style: none; scrollbar-width: none; }

/* Scrollbar fine pour les listes de stats */
.scrollbar-thin::-webkit-scrollbar {
  width: 4px;
}
.scrollbar-thin::-webkit-scrollbar-track {
  background: transparent;
}
.scrollbar-thin::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.3);
  border-radius: 10px;
}
.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: rgba(148, 163, 184, 0.5);
}

/* Styles des liens de navigation (Thème dynamique avec branding) */
.nav-link {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.875rem;
  border-radius: 0.75rem;
  color: var(--sidebar-text-muted);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.nav-link:hover {
  color: var(--sidebar-text);
  background-color: var(--sidebar-hover);
  opacity: 0.8;
}

/* État actif avec couleur de branding */
.nav-link-active {
  color: var(--sidebar-text) !important;
  background-color: var(--sidebar-active) !important;
  font-weight: 600;
  box-shadow: inset 0 0 0 1px var(--sidebar-active);
  opacity: 1;
}

.nav-link-active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 20%;
  bottom: 20%;
  width: 3px;
  background-color: var(--sidebar-text);
  border-radius: 0 4px 4px 0;
  box-shadow: 0 0 8px var(--sidebar-active);
}
</style>